import * as React from 'react';
import { ModalContainer, LabelButton, Icon } from '@teamsnap/snap-ui';

import useShowMerchantAccountWarning from './useShowMerchantAccountWarning';

interface MerchantAccountWarningModalProps {
  orgId: number;
  showManageAccountsButton: boolean;
}

interface MerchantAccountWarningModalViewProps {
  numOfInvalidPaymentsEnabled: number;
  numOfInvalidPayoutsEnabled: number;
  showWarning: boolean;
  closeWarning: () => void;
  goToMerchantAccounts: () => void;
  showManageAccountsButton: boolean;
  showCADWarning?: boolean;
}

const getPaymentWarningMessage = (count: number) => (
  `${count} ${count > 1 ? 'merchant accounts are' : 'merchant account is'} temporarily restricted. ` +
  'Payments and Payouts are disabled until required information is provided and approved.'
);

const getPayoutWarningMessage = (count: number) => (
  `${count}${count > 1 ? ' merchant accounts have ' : ' merchant account has '} payouts disabled. ` +
  'You may still be able to collect payments while payouts are disabled. ' +
  'Please provide required information to resume payouts and to prevent account from moving to temporarily restricted status.'
);

const getPayoutWarningMessageCA = (count: number) => (
  <>
  You have {count} {count > 1 ? 'merchant accounts' : 'merchant account'} with missing information. {count > 1 ? 'Please update them by ' : 'Please update by '}
  <b>February 18th </b>
  to avoid payout disruptions. Failure to complete verification by{' '}
  <b>March 1st </b>
  will result in payment disablement.
  </>
);

const MerchantAccountWarningModal = ({ orgId, showManageAccountsButton }: MerchantAccountWarningModalProps) => {
  const {
    numOfInvalidPaymentsEnabled,
    numOfInvalidPayoutsEnabled,
    showCADWarning,
    showWarning,
    closeWarning,
    goToMerchantAccounts,
  } = useShowMerchantAccountWarning(orgId);

  return (
    <MerchantAccountWarningModalView
      numOfInvalidPaymentsEnabled={numOfInvalidPaymentsEnabled}
      numOfInvalidPayoutsEnabled={numOfInvalidPayoutsEnabled}
      showWarning={showWarning}
      closeWarning={closeWarning}
      goToMerchantAccounts={goToMerchantAccounts}
      showManageAccountsButton={showManageAccountsButton}
      showCADWarning={showCADWarning}
    />
  );
};

export const MerchantAccountWarningModalView = ({
  numOfInvalidPaymentsEnabled,
  numOfInvalidPayoutsEnabled,
  showWarning,
  closeWarning,
  goToMerchantAccounts,
  showManageAccountsButton,
  showCADWarning,
}: MerchantAccountWarningModalViewProps) => (
  <ModalContainer
    disableOverlayClose={true}
    data-testid="MerchantAccountWarningModal--container"
    closeButton={false}
    isOpen={showWarning}
    onClose={closeWarning}
    className="sui-max-w-screen-t sui-p-5"
  >
    <div className="sui-flex sui-items-center">
      <Icon name="report" size="l" filled className="sui-text-negative-icon" />
      <h3 className="sui-ml-2 sui-heading-sm">Missing required information</h3>
    </div>
    <div className="sui-my-4">
      {numOfInvalidPaymentsEnabled > 0 && (
        <p className="sui-mb-2">
          <span data-testid="MerchantAccountWarningModal--account-count">
            {getPaymentWarningMessage(numOfInvalidPaymentsEnabled)}
          </span>
        </p>
      )}
      {numOfInvalidPayoutsEnabled > 0 && (
        <p className="sui-mb-2">
          <span data-testid="MerchantAccountWarningModal--payout-count">
            {showCADWarning 
              ? getPayoutWarningMessageCA(numOfInvalidPayoutsEnabled) 
              : getPayoutWarningMessage(numOfInvalidPayoutsEnabled)}
          </span>
        </p>
      )}
    </div>
    <footer className="sui-flex sui-items-center sui-justify-end sui-mt-2">
      <LabelButton
        data-testid="MerchantAccountWarningModal--close-button"
        labelText="Close"
        variantType="secondary"
        onClick={closeWarning}
      />
      {showManageAccountsButton && (
        <LabelButton
          data-testid="MerchantAccountWarningModal--acccounts-button"
          className="sui-ml-1"
          labelText="Manage accounts"
          variantType="primary"
          onClick={goToMerchantAccounts}
        />
      )}
    </footer>
  </ModalContainer>
);

export default MerchantAccountWarningModal;
